import { PAIRING_CODE_CHARS, PAIRING_CODE_LEN } from "@gooner/shared";

/**
 * What the remote's code field says when a code goes wrong — pure, so the check can pin it
 * without a browser (tools/checks/pairing-message.mts).
 *
 * ONE SENTENCE FOR "WRONG CODE", however it was wrong. A code the server does not know
 * (404) and a code that could never exist (a `0`, an `O`, `000000` — characters outside
 * the pairing alphabet) are the same mistake to the person holding the phone: they read
 * the wrong screen, or misread the right one. Both used to fail differently — the 404 as
 * raw server text with its status prefix ("404: That code isn't valid"), and the
 * impossible characters by being filtered out as they were typed, so `000000` left an
 * empty field and the blank-submit hint, as if nothing had been entered at all.
 */
export const BAD_CODE_MESSAGE =
  "That code didn't match a screen. Enter the code shown on the screen you want to control.";
export const BLANK_CODE_MESSAGE = "Enter the 4-character code shown on your screen.";
export const SHORT_CODE_MESSAGE = "Codes are 4 characters — finish typing, or check the screen.";
export const OFFLINE_MESSAGE = "Couldn't reach goonlords — check your connection and try again.";
export const SERVER_DOWN_MESSAGE = "Something went wrong on our side — try again in a moment.";

const OUTSIDE_ALPHABET = new RegExp(`[^${PAIRING_CODE_CHARS}]`);

/** The field's value as typed: upper-cased, spaces dropped, capped. Characters outside the
 *  alphabet are KEPT so the person can see (and fix) what they typed. */
export function normalizeTypedCode(raw: string): string {
  return raw.toUpperCase().replace(/\s+/g, "").slice(0, PAIRING_CODE_LEN);
}

/** Why `value` cannot be submitted, or null when it is worth asking the server. */
export function pairingInputProblem(value: string): string | null {
  if (value.length === 0) return BLANK_CODE_MESSAGE;
  if (OUTSIDE_ALPHABET.test(value)) return BAD_CODE_MESSAGE;
  if (value.length !== PAIRING_CODE_LEN) return SHORT_CODE_MESSAGE;
  return null;
}

/**
 * The sentence for a failed claim. Duck-typed on `status` (ApiHttpError carries it) so this
 * file needs no API client. Only a 404 is "wrong code"; an outage or a dropped connection
 * must never tell somebody their correct code was wrong.
 */
export function pairingErrorMessage(e: unknown): string {
  const status =
    typeof e === "object" && e !== null && typeof (e as { status?: unknown }).status === "number"
      ? (e as { status: number }).status
      : undefined;
  // No status: fetch itself threw (offline, DNS, CORS) — "Failed to fetch" is not a sentence.
  if (status === undefined) return OFFLINE_MESSAGE;
  if (status === 404) return BAD_CODE_MESSAGE;
  // A proxy's HTML error page is not something to print, and 5xx is never the person's fault.
  if (status >= 500) return SERVER_DOWN_MESSAGE;
  // Other refusals (409 screen limit, 429, 401) carry their own sentence; drop the
  // "409: " prefix http() adds.
  const message = e instanceof Error ? e.message : String(e);
  return message.replace(/^\d{3}:? ?/, "") || SERVER_DOWN_MESSAGE;
}
